import React, { FC, useEffect, useState } from 'react';
import { Redirect, useRouteMatch, useLocation, useHistory } from 'react-router-dom';
import { AuthenticationType } from 'api/public';

import { useGlobalClient } from 'hooks';
import storage from 'storage';
import * as paths from 'paths';

import SignIn from './SignIn';
import SignUp from './SignUp';
import ForgotPassword from './ForgotPassword';
import ForgotPasswordSubmit from './ForgotPasswordSubmit';
import ConfirmSignUp from './ConfirmSignUp';
import Invitation from './Invitation';

import { AuthContainerProps } from '../types';
import { SESSION_KEY_ES_PATH_AND_QUERY, LANDING_PATH } from '../constants';
import Passwordless from './Passwordless';

const SSO_ERROR_PREFIX = 'Error in SAML response processing: PreSignUp failed with error ';
const SIGN_IN_PATHS = [
  paths.login,
  paths.passwordLogin,
  paths.signUp,
  paths.confirmSignup,
  paths.forgotPassword,
  paths.forgotPasswordSubmit,
];

const AuthController: FC = () => {
  const [password, setPassword] = useState('');
  const match = useRouteMatch();
  const location = useLocation();
  const { pathname, search } = location;

  const history = useHistory();

  const client = useGlobalClient();
  const { sso_configuration, authentication_type } = client;
  const clientIsSSO = authentication_type === AuthenticationType.Sso && sso_configuration;
  const clientIsPasswordless = authentication_type === AuthenticationType.Passwordless;
  const urlParams = new URLSearchParams(window.location.search);
  const ssoError = urlParams.get('error_description');

  useEffect(() => {
    if (ssoError) {
      let errorCode = ssoError;
      if (errorCode.startsWith(SSO_ERROR_PREFIX)) {
        errorCode = errorCode.split(SSO_ERROR_PREFIX)[1];
        errorCode = errorCode.substring(0, errorCode.length - 2);
      }
      history.replace(`${paths.authError}?code=${errorCode}`);
    }
  }, [ssoError]);

  if (!ssoError && clientIsSSO && !SIGN_IN_PATHS.includes(match.path)) {
    const isIndex = match.path === paths.index;
    const isLogin = match.path === paths.login;
    const isSignUp = match.path === paths.signUp;
    const isInvitation = match.path === paths.invitation;

    // TODO: @Tim - Could we simply check for a protected route?
    const savePath = search || ((!isIndex || !search) && !isLogin && !isSignUp && !isInvitation);

    if (savePath) {
      window.sessionStorage.setItem(SESSION_KEY_ES_PATH_AND_QUERY, pathname + search);
    }

    return <Redirect to={paths.sso} />;
  }

  const mainProps: AuthContainerProps = {
    authData: {
      username: storage.userEmail,
      password,
    },
    updateUsername(newUserName: string) {
      storage.userEmail = newUserName;
    },
    updatePassword(newPassword: string) {
      setPassword(newPassword);
    },
  };

  let Main: FC<AuthContainerProps> = null;

  const landingPath = urlParams.get('landing_path');

  switch (match.path) {
    case paths.index:
      Main = clientIsPasswordless ? Passwordless : SignIn;
      break;
    case paths.login:
      // If a landing path was specified then save it
      // so that the user can be redirected after login
      if (landingPath) {
        window.localStorage.setItem(LANDING_PATH, decodeURIComponent(landingPath));
      }
      Main = clientIsPasswordless ? Passwordless : SignIn;
      break;
    case paths.passwordLogin:
      Main = SignIn;
      break;
    case paths.signUp:
      Main = SignUp;
      break;
    case paths.forgotPassword:
      Main = ForgotPassword;
      break;
    case paths.forgotPasswordSubmit:
      Main = ForgotPasswordSubmit;
      break;
    case paths.confirmSignup:
      Main = ConfirmSignUp;
      break;
    case paths.invitation:
      Main = clientIsPasswordless ? Passwordless : Invitation;
      break;
    default:
      Main = SignIn;
  }

  return <Main {...mainProps} />;
};

export default AuthController;
